import {
    Box,
    Button,
    Card,
    CardBody,
    Flex,
    Heading,
    Text,
    Badge,
    VStack,
    HStack,
    SimpleGrid,
    useColorModeValue,
    Icon,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Input,
    InputGroup,
    InputLeftElement,
} from "@chakra-ui/react";
import { IconBuilding, IconArrowRight, IconUser, IconSearch, IconLink } from "@tabler/icons-react";
import { FunctionComponent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Page } from "../Page";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import { useGetClients } from "../../api/clients/clientQueries";
import { useCurrentUser } from "../../api/users/userQueries";
import { CreateOrEditClientModal } from "../SystemAdminPage/ClientPanel/CreateOrEditClientModal";
import { DropdownSearch } from "../../components/DropdownSearch/DropdownSearch";

interface ClientCardProps {
    client: {
        id: string;
        name: string;
        agressoCode?: string | null;
        linkerFirstName?: string | null;
        linkerLastName?: string | null;
        description?: string | null;
        abbreviation?: string | null;
    };
    onNavigate: (clientId: string) => void;
}

const ClientCard: FunctionComponent<ClientCardProps> = ({ client, onNavigate }) => {
    const cardBg = useColorModeValue("white", "gray.800");
    const cardHover = useColorModeValue("gray.50", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");

    const leadMD = client.linkerFirstName && client.linkerLastName ? `${client.linkerFirstName} ${client.linkerLastName}` : "Not assigned";

    return (
        <Card
            bg={cardBg}
            border="1px"
            borderColor={borderColor}
            transition="all 0.2s ease-in-out"
            cursor="pointer"
            _hover={{
                bg: cardHover,
                transform: "translateY(-2px)",
                boxShadow: "lg",
            }}
            onClick={() => onNavigate(client.id)}
            role="button"
            tabIndex={0}
            overflow="auto"
        >
            <CardBody>
                <VStack align="start" spacing={3}>
                    <HStack justify="space-between" w="full">
                        <HStack spacing={3} align="center">
                            <Icon as={IconBuilding} color="blue.500" boxSize={5} />
                            <Heading size="md" fontWeight="semibold">
                                {client.name}
                            </Heading>
                        </HStack>
                        <Icon as={IconArrowRight} color="gray.400" boxSize={4} />
                    </HStack>

                    <VStack align="start" spacing={2} w="full">
                        {client.agressoCode && (
                            <HStack>
                                <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                    {client.agressoCode}
                                </Badge>
                                {client.abbreviation && (
                                    <Badge colorScheme="gray" variant="subtle" fontSize="xs">
                                        {client.abbreviation}
                                    </Badge>
                                )}
                            </HStack>
                        )}

                        <HStack fontSize="sm" spacing={2} align="center">
                            <Icon as={IconUser} boxSize={4} />
                            <Text>{leadMD}</Text>
                        </HStack>

                        {client.description && (
                            <Text fontSize="sm" noOfLines={2}>
                                {client.description}
                            </Text>
                        )}
                    </VStack>

                    <HStack justify="end" w="full" pt={2}>
                        <Button
                            size="sm"
                            variant="outline"
                            colorScheme="blue"
                            rightIcon={<Icon as={IconArrowRight} />}
                            onClick={e => {
                                e.stopPropagation();
                                onNavigate(client.id);
                            }}
                        >
                            Enter Portal
                        </Button>
                    </HStack>
                </VStack>
            </CardBody>
        </Card>
    );
};

export const ClientListPage: FunctionComponent = () => {
    const navigate = useNavigate();
    const { data: clients = [], isLoading: isLoadingClients, error: clientsError, refetch: refetchClients } = useGetClients();
    const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
    const pageName = "Transfer Pricing Clients";
    const [showCreateOrEditClientDialog, setShowCreateOrEditClientDialog] = useState(false);

    const transformedClients = useMemo(
        () =>
            clients.map(c => ({
                id: c.id,
                name: c.name,
                agressoCode: c.agressoCode,
                leadMD: c.leadMD,
                industryAnalysis: c.industryAnalysisBlobPath,
                orgDiagram: c.orgDiagramBlobPath,
                abbreviation: c.abbreviation,
                headcount: c.headcount,
                description: c.description,
                endingDate: c.endingDate,
                industry: c.industry,
                overviewOfIndustry: c.overviewOfIndustry,
                productsAndServices: c.productsAndServices,
                majorPlayers: c.majorPlayers,
                industryPerformance: c.industryPerformance,
                competitiveLandscape: c.competitiveLandscape,
                keyExternalDrivers: c.keyExternalDrivers,
                barriersToEntry: c.barriersToEntry,
            })),
        [clients],
    );

    // State for search functionality
    const [searchTerm, setSearchTerm] = useState<string>("");

    // Call hooks consistently at the top level
    const cardBg = useColorModeValue("gray.50", "gray.700");
    const searchInputBg = useColorModeValue("white", "gray.800");
    const searchInputBorderColor = useColorModeValue("gray.300", "gray.600");
    const searchInputHoverBorderColor = useColorModeValue("gray.400", "gray.500");

    const handleClientNavigation = (clientId: string) => {
        navigate(`/clients/${clientId}/dashboard`);
    };

    // Filter clients based on search term
    const filteredClients = clients.filter(client => {
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();
        return (
            client.name.toLowerCase().includes(searchLower) ||
            client.agressoCode?.toLowerCase().includes(searchLower) ||
            client.abbreviation?.toLowerCase().includes(searchLower) ||
            client.description?.toLowerCase().includes(searchLower) ||
            `${client.linkerFirstName || ""} ${client.linkerLastName || ""}`.toLowerCase().includes(searchLower)
        );
    });

    if (isLoadingUser || isLoadingClients) {
        return (
            <Page title={pageName} topNavProps={{ showLogo: true }}>
                <Flex justify="center" align="center" height="400px">
                    <VStack spacing={4}>
                        <Spinner size="xl" color="blue.500" />
                        <Text fontSize="lg" color="gray.600">
                            Loading your client...
                        </Text>
                    </VStack>
                </Flex>
            </Page>
        );
    }

    if (clientsError) {
        return (
            <Page title={pageName} topNavProps={{ showLogo: true }}>
                <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertTitle>Unable to load client portals</AlertTitle>
                    <AlertDescription>
                        There was an error loading your client information. Please ensure you have not blocked pop-ups. If you continue to face issues, please
                        log out and log back in or contact your administrator.
                    </AlertDescription>
                </Alert>
            </Page>
        );
    }

    const userName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}`.trim() || currentUser.email : "";

    function closeCreateOrEditClientDialog() {
        setShowCreateOrEditClientDialog(false);
        refetchClients();
    }

    return (
        <Page
            title={pageName}
            topNavProps={{
                showLogo: true,
                menu: <DropdownSearch />,
            }}
        >
            <VStack align="start" spacing={16} w="full">
                {/* Welcome Section */}
                <Box w="full">
                    <VStack align="start" spacing={4}>
                        <Heading size="lg" color="gray.400">
                            Welcome
                            {userName && (
                                <>
                                    ,{" "}
                                    <Text as="span" fontWeight="bold">
                                        {userName}
                                    </Text>
                                </>
                            )}
                        </Heading>
                        <Text fontSize="lg" color="gray.400">
                            Select a client portal to access transfer pricing tools and resources:
                        </Text>
                    </VStack>
                </Box>

                {/* Clients Section */}
                <Box w="full">
                    <SectionHeader title={`Available Client Portals (${filteredClients.length})`} />

                    {/* Search Input */}
                    <Flex mt={4} mb={6} gap="1rem">
                        <InputGroup maxW="400px">
                            <InputLeftElement pointerEvents="none">
                                <Icon as={IconSearch} color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="Search clients by name, code, or contact..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                bg={searchInputBg}
                                borderColor={searchInputBorderColor}
                                _hover={{
                                    borderColor: searchInputHoverBorderColor,
                                }}
                                _focus={{
                                    borderColor: "blue.500",
                                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
                                }}
                            />
                        </InputGroup>

                        <Button leftIcon={<IconLink />} variant="primary" flexShrink="0" onClick={() => setShowCreateOrEditClientDialog(true)}>
                            Link Engage Client
                        </Button>
                    </Flex>

                    {filteredClients.length === 0 ? (
                        <Card mt={6} bg={cardBg} borderStyle="dashed">
                            <CardBody textAlign="center" py={12}>
                                <VStack spacing={4}>
                                    <Icon as={IconBuilding} boxSize={12} color="gray.400" />
                                    <VStack spacing={2}>
                                        <Heading size="md" color="gray.400">
                                            {searchTerm ? "No Matching Client Portals" : "No Client Portals Available"}
                                        </Heading>
                                        <Text color="gray.400" fontSize="md">
                                            {searchTerm
                                                ? `No client portals match "${searchTerm}". Try adjusting your search terms.`
                                                : "You don't have access to any client portals yet. Please contact your administrator to request access."}
                                        </Text>
                                    </VStack>
                                </VStack>
                            </CardBody>
                        </Card>
                    ) : (
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mt={6}>
                            {filteredClients.map(client => (
                                <ClientCard key={client.id} client={client} onNavigate={handleClientNavigation} />
                            ))}
                        </SimpleGrid>
                    )}
                </Box>
            </VStack>
            <CreateOrEditClientModal
                show={showCreateOrEditClientDialog}
                clientData={undefined}
                onClose={closeCreateOrEditClientDialog}
                clientNames={transformedClients ?? []}
                isEdit={false}
            />
        </Page>
    );
};
